import { Form, RadioField, useFormContext } from '@calendly/form';
import { Fieldset } from '@calendly/form/parts';
import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { AccordionBanner, Header, Body } from '@calendly/ui/components/accordion-banner';
import { Sheet } from '@calendly/ui/components/sheet';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { useEffect, forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { DOMAIN_CONTROL } from 'constants/billing_features';
import { isAvailableFeatureSelector } from 'features/billing';
import AllowJoinOrgIcon from 'images/icons/allow-join-an-org.svg';
import DomainControlIcon from 'images/icons/domain-control.svg';
import HideYourCompanyIcon from 'images/icons/hide-your-company.svg';
import { currentOrganizationSelector } from 'selectors/current_organization';
import { useUpdateOrganizationMutation } from '../api';
import { Paywall } from './paywall';
import { DomainCards } from '../domain_control/cards/cards';
import { CreateDomainControl } from '../domain_control/create_domain_control';
import { DeleteDomainPopup } from '../domain_control/delete_domain_popup';
import { DomainControlSettings } from '../domain_control/domain_control_settings/domain_control_settings';
import { EnableDomainControlPopup } from '../domain_control/enable_domain_control_popup';
const formFieldsContainerCss = "f1mcj5wl";
const FormFieldsContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(formFieldsContainerCss, props.className)} />;
});
const optionBoundingBoxSelectedCss = "o1c1ol2c";
const optionBoundingBoxCss = "oz24ntv";
const OptionBoundingBox = forwardRef((props, ref) => {
  const {
    selected
  } = props;
  return <Sheet ref={ref} {...props} className={classNames(optionBoundingBoxCss, {
    [optionBoundingBoxSelectedCss]: selected
  }, props.className)} />;
});
const domainControlContainerCss = "d1w83ta8";
const DomainControlContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(domainControlContainerCss, props.className)} />;
});
const styledSvgCss = "s1c3hsdz";
const StyledSvg = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'svg';
  return <Component ref={ref} {...restProps} className={classNames(styledSvgCss, props.className)} />;
});
const Triangle = () => <StyledSvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
    <line x1="0" y1="0" x2="100%" y2="0" stroke="white" strokeWidth="32px" />
    <line x1="0" y1="0" x2="50%" y2="50%" strokeWidth="10px" />
    <line x1="50%" y1="50%" x2="100%" y2="0" strokeWidth="10px" />
  </StyledSvg>;
const optionContainerIsPhoneCss = "o1t3w4sz";
const optionContainerCss = "ou9x691";
const OptionContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(optionContainerCss, {
    [optionContainerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const optionContentContainerCss = "ozcu25i";
const OptionContentContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(optionContentContainerCss, props.className)} />;
});
const optionIconCss = "orwbl8r";
const OptionIcon = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(optionIconCss, props.className)} />;
});
const optionTitleCss = "o1w6faj1";
const OptionTitle = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(optionTitleCss, props.className)} />;
});
const optionDescriptionCss = "o1kbor4k";
const OptionDescription = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(optionDescriptionCss, props.className)} />;
});
const allowIconCss = "angtunw";
const AllowIcon = forwardRef((props, ref) => <AllowJoinOrgIcon ref={ref} {...props} className={classNames(allowIconCss, props.className)} />);
const hideIconCss = "h12yo5d2";
const HideIcon = forwardRef((props, ref) => <HideYourCompanyIcon ref={ref} {...props} className={classNames(hideIconCss, props.className)} />);
const domainIconCss = "d1bdvw35";
const DomainIcon = forwardRef((props, ref) => <DomainControlIcon ref={ref} {...props} className={classNames(domainIconCss, props.className)} />);
const dividerIsPhoneCss = "d1k7qiwp";
const dividerCss = "d1q9nkst";
const Divider = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'hr';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(dividerCss, {
    [dividerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const styledSheetCss = "s18hallj";
const StyledSheet = forwardRef((props, ref) => <Sheet ref={ref} {...props} className={classNames(styledSheetCss, props.className)} />);
const styledAccordionBannerCss = "stpr4kv";
const StyledAccordionBanner = forwardRef((props, ref) => <AccordionBanner ref={ref} {...props} className={classNames(styledAccordionBannerCss, props.className)} />);
const styledBodyCss = "s1k8jhdp";
const StyledBody = forwardRef((props, ref) => <Body ref={ref} {...props} className={classNames(styledBodyCss, props.className)} />);
const styledHeaderCss = "s1xb8ev9";
const StyledHeader = forwardRef((props, ref) => <Header ref={ref} {...props} className={classNames(styledHeaderCss, props.className)} />);
const UserAccessOptions = ({
  seePaywall
}) => {
  const {
    values,
    submitForm,
    touched: {
      user_access_kind: touched
    }
  } = useFormContext();
  const {
    owner: {
      email
    }
  } = useSelector(currentOrganizationSelector);
  const domain = email?.split('@')[1];
  useEffect(() => {
    if (touched) submitForm();
  }, [values.user_access_kind, submitForm, touched]);
  return <FormFieldsContainer>
      <Fieldset>
        <OptionBoundingBox selected={values.user_access_kind === 'allow_join_org'}>
          <RadioField bulletPosition="top" name="user_access_kind" value="allow_join_org" label={<OptionContainer>
                <OptionIcon>
                  <AllowIcon />
                </OptionIcon>
                <OptionContentContainer>
                  <OptionTitle>
                    {I18n.t('publisher.admin.login.page.user_access.form_options.allow_join_an_org.title')}
                  </OptionTitle>
                  <OptionDescription>
                    {HtmlParser(I18n.t('publisher.admin.login.page.user_access.form_options.allow_join_an_org.description', {
                domain
              }))}
                  </OptionDescription>
                </OptionContentContainer>
              </OptionContainer>} />
        </OptionBoundingBox>
        <OptionBoundingBox selected={values.user_access_kind === 'hidden'}>
          <RadioField bulletPosition="top" name="user_access_kind" value="hidden" label={<OptionContainer>
                <OptionIcon>
                  <HideIcon />
                </OptionIcon>
                <OptionContentContainer>
                  <OptionTitle>
                    {I18n.t('publisher.admin.login.page.user_access.form_options.hide_your_company.title')}
                  </OptionTitle>
                  <OptionDescription>
                    {HtmlParser(I18n.t('publisher.admin.login.page.user_access.form_options.hide_your_company.description', {
                domain
              }))}
                  </OptionDescription>
                </OptionContentContainer>
              </OptionContainer>} />
        </OptionBoundingBox>
        {!seePaywall && <OptionBoundingBox selected={values.user_access_kind === 'domain_control'}>
            <RadioField bulletPosition="top" name="user_access_kind" value="domain_control" label={<OptionContainer>
                  <OptionIcon>
                    <DomainIcon />
                  </OptionIcon>
                  <OptionContentContainer>
                    <OptionTitle>
                      {I18n.t('publisher.admin.login.page.user_access.form_options.domain_control.title')}
                    </OptionTitle>
                    <OptionDescription>
                      {I18n.t('publisher.admin.login.page.user_access.form_options.domain_control.description')}
                    </OptionDescription>
                  </OptionContentContainer>
                </OptionContainer>} />
            {values.user_access_kind === 'domain_control' && <Triangle />}
          </OptionBoundingBox>}
      </Fieldset>
    </FormFieldsContainer>;
};
export const FormContent = () => {
  const seePaywall = !useSelector(state => isAvailableFeatureSelector(state, {
    featureKey: DOMAIN_CONTROL
  }));
  const {
    user_access_kind: kind
  } = useSelector(currentOrganizationSelector);
  const [updateOrganization] = useUpdateOrganizationMutation();
  return <>
      <Form initialValues={{
      user_access_kind: kind
    }} onSubmit={settings => updateOrganization(settings)}>
        <UserAccessOptions seePaywall={seePaywall} />
        {seePaywall && <>
          <Divider />
          <Paywall />
        </>}

      </Form>
      {!seePaywall && kind === 'domain_control' && <DomainControlContainer>
        <StyledSheet>
          <StyledAccordionBanner>
            <StyledHeader>
              {I18n.t('publisher.admin.login.page.user_access.form_options.domain_control.verify_domains')}
            </StyledHeader>
            <StyledBody>
              <CreateDomainControl />
              <DomainCards />
            </StyledBody>
          </StyledAccordionBanner>
        </StyledSheet>
        <StyledSheet>
          <StyledAccordionBanner>
            <StyledHeader>
              {I18n.t('publisher.admin.login.page.user_access.form_options.domain_control.customize_domain_control_headings')}
            </StyledHeader>
            <StyledBody>
              <DomainControlSettings />
            </StyledBody>
          </StyledAccordionBanner>
        </StyledSheet>
        <DeleteDomainPopup />
        <EnableDomainControlPopup />
      </DomainControlContainer>}
    </>;
};

require("./form_content.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./form_content.js");